import { FC, useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import ReusableModal from '../common/Modal';
import {
  ForkKnife,
  SealQuestion,
  Chair,
  PencilSimple,
} from '@phosphor-icons/react';
import { useTemplates } from '../../hooks/useTemplates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateAgentTemplateApi } from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';

interface EditAgentTypeModalProps {
  open: boolean;
  onClose: () => void;
  agentId: string;
  currentTemplateId: string;
}

interface Template {
  _id: string;
  name: string;
  desc: string;
}

const EditAgentTypeModal: FC<EditAgentTypeModalProps> = ({
  open,
  onClose,
  agentId,
  currentTemplateId,
}) => {
  const { data: templatesData, isLoading: isTemplatesLoading } = useTemplates();
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<string>(currentTemplateId);
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();

  const { mutateAsync: updateAgentTemplate, isPending: isUpdatePending } =
    useMutation({
      mutationFn: updateAgentTemplateApi,
      onSuccess: () => {
        showAlert('Agent type updated successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['agents'] });
        onClose();
      },
      onError: (error: any) => {
        showAlert(
          error?.response?.data?.message || 'Failed to update agent type',
          'error'
        );
      },
    });

  const getTemplateIcon = (templateName: string) => {
    switch (templateName) {
      case 'Food Ordering':
        return <ForkKnife size={16} color="#6B6F80" />;
      case 'General Queries':
        return <SealQuestion size={16} color="#6B6F80" />;
      case 'Automate Reservation':
        return <Chair size={16} color="#6B6F80" />;
      default:
        return <SealQuestion size={16} color="#6B6F80" />;
    }
  };

  const getTemplateName = (templateName: string) => {
    switch (templateName) {
      case 'Food Ordering':
        return 'Food Ordering';
      case 'General Queries':
        return 'General FAQ';
      case 'Automate Reservation':
        return 'Reservation';
      default:
        return templateName;
    }
  };

  const handleUpdateTemplate = async () => {
    if (selectedTemplateId === currentTemplateId) {
      showAlert('Please select a different template to update', 'error');
      return;
    }

    try {
      await updateAgentTemplate({
        agentId,
        templateId: selectedTemplateId,
      });
    } catch (error) {
      console.error('Error updating agent template:', error);
    }
  };

  const templateBoxStyles = (isSelected: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    cursor: 'pointer',
    padding: '12px',
    border: '1px solid',
    borderColor: isSelected ? '#6E61F0' : '#DCE0E7',
    borderRadius: '8px',
    backgroundColor: isSelected ? 'rgba(110, 97, 240, 0.04)' : '#FFFFFF',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: isSelected
        ? 'rgba(110, 97, 240, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
      borderColor: isSelected ? '#6E61F0' : '#B0B4C1',
    },
  });

  const iconBoxStyles = (isSelected: boolean) => ({
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    backgroundColor: isSelected ? '#FFFFFF' : '#F8F9FC',
    border: '1px solid #DCE0E7',
  });

  const templates = templatesData?.data?.templates || [];

  return (
    <>
      <ReusableModal
        open={open}
        onClose={onClose}
        title="Edit Agent Type"
        width={600}
        backgroundColor="#F8F9FC"
      >
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
          <Typography
            fontSize={14}
            fontWeight={400}
            lineHeight={1.5}
            letterSpacing={-0.14}
            color="text.secondary"
            mb={3}
          >
            Select a new template type for your agent. This will update the
            agent's configuration and behavior.
          </Typography>

          {isTemplatesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}
            >
              {templates.map((template: Template) => (
                <Box
                  key={template._id}
                  onClick={() => setSelectedTemplateId(template._id)}
                  sx={templateBoxStyles(selectedTemplateId === template._id)}
                >
                  <Box sx={iconBoxStyles(selectedTemplateId === template._id)}>
                    {getTemplateIcon(template.name)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: '#302B3F',
                        fontFamily: '"DM Sans"',
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: 'normal',
                        letterSpacing: '-0.14px',
                        mb: 0.5,
                      }}
                    >
                      {getTemplateName(template.name)}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#6B6F80',
                        fontFamily: '"DM Sans"',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: 'normal',
                        letterSpacing: '-0.12px',
                      }}
                    >
                      {template.desc}
                    </Typography>
                  </Box>
                  {selectedTemplateId === template._id && (
                    <Box
                      sx={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#6E61F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#FFFFFF',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              pt: 2,
              borderTop: '1px solid #DCE0E7',
              mx: -2,
              px: 2,
            }}
          >
            <Button
              onClick={onClose}
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                flex: 1,
                color: 'custom.textBlue',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleUpdateTemplate}
              startIcon={
                isUpdatePending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PencilSimple size={16} />
                )
              }
              disabled={
                isUpdatePending || selectedTemplateId === currentTemplateId
              }
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                flex: 1,
                backgroundColor: '#6E61F0',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#5A4FD4',
                },
                '&:disabled': {
                  backgroundColor: '#B0B4C1',
                },
              }}
            >
              {isUpdatePending ? 'Updating...' : 'Update Agent Type'}
            </Button>
          </Box>
        </Box>
      </ReusableModal>
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default EditAgentTypeModal;
